import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, Menu, Icon } from 'semantic-ui-react';
import { getAllCompanies, getStudents } from '../../../services/apiService';
import { getFullName } from '../../../utils';
import { ActivityList } from './_components/ActivityList';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import useFilterPlacementSpoc from '../_utils/useFilterPlacementSpoc';
import usePaginationState from '../_utils/usePaginationState';

function useOutsideAlerter(ref, setShowDateMenu) {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setShowDateMenu(false);
      }
    }
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref]);
}

const Activities = () => {
  const [companyIds, setCompanyIds] = useState([]);
  const [studentIds, setStudentIds] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [students, setStudents] = useState([]);
  const [activeItem, setActiveItem] = useState('Admin');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [showDateMenu, setShowDateMenu] = useState(false);
  const wrapperRef = useRef(null);

  const {
    page: studentPage,
    setPage: setStudentPage,
    totalCount: studentTotalCount,
    setTotalCount: setStudentTotalCount,
  } = usePaginationState();

  const {
    page: adminPage,
    setPage: setAdminPage,
    totalCount: adminTotalCount,
    setTotalCount: setAdminTotalCount,
  } = usePaginationState();
  const {
    loading,
    placementSpocOptions,
    placementSpocFilter,
    setPlacementSpocFilter,
  } = useFilterPlacementSpoc();

  useOutsideAlerter(wrapperRef, setShowDateMenu);
  const handleMenuClick = (e, { name }) => setActiveItem(name);

  // fetch companies
  const fetchStudentOptions = async () => {
    try {
      // setLoading(true);
      const _students = await getStudents();
      // setLoading(false);
      setStudents(_students?.data ?? []);
    } catch (error) {
      // setLoading(false);
      console.log(error);
    }
  };
  // fetch companies
  const fetchCompanyOptions = async () => {
    try {
      // setLoading(true);
      const _companies = await getAllCompanies();
      // setLoading(false);
      setCompanies(_companies?.data ?? []);
    } catch (error) {
      // setLoading(false);
      console.log(error);
    }
  };

  const renderDateRangeFilterText = () => {
    if (startDate && endDate) {
      return (
        <p>
          {`${moment(startDate).format('DD/MM/YYYY')} - ${moment(
            endDate
          ).format('DD/MM/YYYY')}`}
          <span
            className='ml-10'
            onClick={() => {
              setStartDate(null);
              setEndDate(null);
            }}
          >
            {' '}
            <Icon name='close' />{' '}
          </span>
        </p>
      );
    } else if (startDate) {
      return (
        <p>
          {`${moment(startDate).format('DD/MM/YYYY')} - `}
          <span
            className='ml-10'
            onClick={() => {
              setStartDate(null);
              setEndDate(null);
            }}
          >
            <Icon name='close' />{' '}
          </span>
        </p>
      );
    } else if (endDate) {
      return (
        <p>
          {` - ${moment(endDate).format('DD/MM/YYYY')}`}
          <span
            className='ml-10'
            onClick={() => {
              setStartDate(null);
              setEndDate(null);
            }}
          >
            {' '}
            <Icon name='close' />{' '}
          </span>
        </p>
      );
    } else {
      return 'Date';
    }
  };

  useEffect(() => {
    fetchStudentOptions();
    fetchCompanyOptions();
  }, []);

  return (
    <div>
      <div className='flex items-center justify-between my-5 mb-30'>
        <h1 className='text-3xl font-semibold user-heading '>Activities</h1>
        <div className='flex items-center gap-4 w-95'>
          {placementSpocOptions.length > 0 && activeItem === 'Admin' && (
            <Dropdown
              placeholder='Placement Spoc'
              options={placementSpocOptions}
              search
              multiple
              selection
              clearable
              defaultValue={placementSpocFilter}
              onChange={(e, { value }) => {
                if (activeItem === 'Admin') {
                  setAdminPage(0);
                }
                setPlacementSpocFilter(value);
              }}
            />
          )}

          <div className='w-30'>
            <Dropdown
              options={students.map((student) => ({
                key: student?.userId,
                text: getFullName(student?.userDetails),
                value: student.userId,
                image: { avatar: true, src: student?.userDetails?.thumbnail },
              }))}
              onChange={(e, { value, text }) => {
                if (activeItem === 'Admin') {
                  setAdminPage(0);
                }
                if (activeItem === 'Student') {
                  setStudentPage(0);
                }
                setStudentIds(value);
              }}
              placeholder='User'
              search
              selection
              multiple
              clearable
              fluid
              value={studentIds}
            />
          </div>
          <div className='w-30'>
            <div
              onClick={() => setShowDateMenu(true)}
              className='relative flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded cursor-pointer'
            >
              <p>{renderDateRangeFilterText()}</p>
              {showDateMenu && (
                <div ref={wrapperRef} className='absolute right-0 top-12'>
                  <Menu vertical>
                    <Menu.Item>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-3 mr-10 '>
                          <p>Start Date</p>
                        </div>
                        <DatePicker
                          selected={
                            startDate ? new Date(startDate) : new Date()
                          }
                          dateFormat='dd/MM/yyyy'
                          onChange={(date) => {
                            if (activeItem === 'Admin') {
                              setAdminPage(0);
                            }
                            if (activeItem === 'Student') {
                              setStudentPage(0);
                            }
                            setStartDate(date);
                          }}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </Menu.Item>
                    <Menu.Item>
                      <div className='flex items-center justify-between'>
                        <div className='flex items-center gap-3 mr-10'>
                          <p>End Date</p>
                        </div>
                        <DatePicker
                          selected={endDate ? new Date(endDate) : new Date()}
                          dateFormat='dd/MM/yyyy'
                          onChange={(date) => {
                            if (activeItem === 'Admin') {
                              setAdminPage(0);
                            }
                            if (activeItem === 'Student') {
                              setStudentPage(0);
                            }
                            setEndDate(date);
                          }}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </Menu.Item>
                  </Menu>
                </div>
              )}
            </div>
          </div>
          {/* <div className='w-30'>
            <Dropdown
              options={companies.map((company) => ({
                key: company.companyId,
                text: company.companyName,
                value: company.companyId,
                image: { avatar: true, src: company.companyAvatar },
              }))}
              onChange={(e, { value, text }) => {
                // find the company details from the companies array
                const _company = companies.find(
                  (company) => company.companyId === value
                );
                setCompanyDetails({
                  ...companyDetails,
                  companyId: value,
                  companyName: _company?.companyName ?? '',
                  companyAvatar: _company?.companyAvatar ?? '',
                });
              }}
              placeholder='round'
              search
              selection
              clearable
              fluid
              value={companyDetails.companyId || ''}
            />
          </div> */}
          <div className='w-30'>
            <Dropdown
              options={companies.map((company) => ({
                key: company.companyId,
                text: company.companyName,
                value: company.companyId,
                image: { avatar: true, src: company.companyAvatar },
              }))}
              onChange={(e, { value, text }) => {
                if (activeItem === 'Admin') {
                  setAdminPage(0);
                }
                if (activeItem === 'Student') {
                  setStudentPage(0);
                }
                setCompanyIds(value);
              }}
              placeholder='company'
              search
              selection
              multiple
              clearable
              value={companyIds}
            />
          </div>
        </div>
      </div>
      <div className='mt-15'>
        <div>
          <div className='flex items-center justify-between bg-white rounded'>
            <Menu pointing secondary color='red'>
              <Menu.Item
                name='Admin'
                active={activeItem === 'Admin'}
                onClick={handleMenuClick}
              />
              <Menu.Item
                name='Student'
                active={activeItem === 'Student'}
                onClick={handleMenuClick}
              />
            </Menu>
          </div>

          <div className='my-1 bg-white rounded w-100'>
            <ActivityList
              filters={{
                userIds: studentIds,
                companyIds,
                dateRangeStart: moment(startDate).toISOString(),
                dateRangeEnd: moment(endDate).toISOString(),
              }}
              placementSpocFilter={placementSpocFilter}
              loading={loading}
              listType={activeItem}
              adminPage={adminPage}
              setAdminPage={setAdminPage}
              adminTotalCount={adminTotalCount}
              setAdminTotalCount={setAdminTotalCount}
              studentPage={studentPage}
              setStudentPage={setStudentPage}
              studentTotalCount={studentTotalCount}
              setStudentTotalCount={setStudentTotalCount}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Activities;
